import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { emergencyHelplines } from "@/mocks/siteContent";
import type { Category } from "@/types/directory";
import { categories as fallbackCategories } from "@/mocks/categories";
import { getCategories } from "@/services/directoryService";

const LOGO_URL =
  "https://storage.helloreaddy.io/project_files/9a028e4c-ff87-4d3e-a810-ef1048f4ad5f/d01b20cb-70c5-407a-bc10-7f897ccd03bd_compressed_coastal-logo-Photoroom.webp";

const exploreLinks = [
  { label: "Featured Listings", to: "/featured-listings" },
  { label: "All Listings", to: "/directory" },
  { label: "All Locations", to: "/locations" },
  { label: "Listing Plans", to: "/listing-plans" },
  { label: "Add Your Business", to: "/list-your-business" },
  { label: "About Us", to: "/about" },
  { label: "Contact Us", to: "/contact" },
];

const districtLinks = [
  { label: "South Canara", to: "/location/south-canara" },
  { label: "Udupi", to: "/location/udupi" },
  { label: "North Canara", to: "/location/north-canara" },
];

export default function Footer() {
  const [categories, setCategories] = useState<Category[]>(fallbackCategories);

  useEffect(() => {
    let active = true;
    getCategories()
      .then((list) => {
        if (active && list.length) setCategories(list);
      })
      .catch(() => {
        /* keep the fallback list so the footer always renders */
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      {/* Popular Search — horizontal responsive menu sitting just above the footer */}
      <section aria-label="Popular Search" className="relative border-t border-background-200 bg-background-100">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/60 to-transparent"
        />
        <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-search-line text-base" />
              </span>
              <div className="min-w-0">
                <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground-950">
                  Popular Search
                </h4>
                <p className="text-xs text-foreground-600">
                  Browse Most Essential Categories
                </p>
              </div>
            </div>
            <Link
              to="/categories-a-z"
              className="group inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 self-start whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors duration-200 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800 sm:self-auto"
            >
              View all categories
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-base transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </Link>
          </div>
          <nav className="no-scrollbar -mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:gap-2.5 md:overflow-visible md:px-0">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 py-1.5 pl-2.5 pr-3.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700 md:text-sm"
              >
                <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-100 text-primary-700 transition-colors duration-200 group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${category.icon} text-sm`} />
                </span>
                {category.name}
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <footer className="bg-primary-900 text-background-200">
        <div className="border-b border-primary-700 bg-primary-950">
          <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-md">
                <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-red-400/40 bg-red-600/20 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-red-100">
                  <span className="flex h-2 w-2 items-center justify-center">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-red-400" />
                  </span>
                  24&times;7 Emergency Helplines
                </span>
                <h2 className="mt-3 font-heading text-lg font-bold text-background-50">
                  In an emergency, call first — then find help near you
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-background-300">
                  These national numbers work from any phone, free of cost, across South Canara, Udupi
                  and North Canara.
                </p>
              </div>

              <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4 lg:w-auto lg:min-w-[560px]">
                {emergencyHelplines.map((line) => (
                  <a
                    key={line.id}
                    href={`tel:${line.number}`}
                    title={`Call ${line.number} — ${line.label}`}
                    className="group flex cursor-pointer items-center gap-3 rounded-lg border border-primary-700 bg-primary-900 p-3 transition-colors hover:border-red-400 hover:bg-primary-800"
                  >
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-red-600 text-white">
                      <i className={`${line.icon} text-lg`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-heading text-xl font-extrabold leading-none text-background-50">
                        {line.number}
                      </span>
                      <span className="mt-1 block text-[11px] font-semibold text-background-200">
                        {line.label}
                      </span>
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
            <div>
              <div className="inline-flex rounded-md bg-background-50 p-3">
                <img
                  src={LOGO_URL}
                  alt="Coastal Bizz Connect"
                  title="Coastal Bizz Connect"
                  className="h-12 w-auto"
                />
              </div>
              <p className="mt-5 max-w-sm text-sm leading-relaxed text-background-300">
                The trusted hyperlocal business directory for coastal Karnataka. Discover, compare and
                connect with verified local businesses across South Canara, Udupi and North Canara.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {districtLinks.map((district) => (
                  <Link
                    key={district.label}
                    to={district.to}
                    className="cursor-pointer rounded-full border border-primary-700 px-3 py-1 text-xs font-medium text-background-200 transition-colors hover:border-accent-500 hover:text-accent-300"
                  >
                    {district.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-background-50">
                Explore
              </h4>
              <ul className="mt-4 space-y-2.5">
                {exploreLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-background-300 transition-colors hover:text-accent-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-background-50">
                Get in Touch
              </h4>
              <ul className="mt-4 space-y-3.5 text-sm text-background-300">
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-400">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <a href="mailto:info@coastalbizzconnect.com" className="transition-colors hover:text-accent-300">
                    info@coastalbizzconnect.com
                  </a>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-400">
                    <i className="ri-phone-line text-base" />
                  </span>
                  <a href="tel:+919876543210" className="transition-colors hover:text-accent-300">
                    +91 98765 43210
                  </a>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-400">
                    <i className="ri-map-pin-line text-base" />
                  </span>
                  <span>Mangaluru, Dakshina Kannada, Karnataka</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-400">
                    <i className="ri-file-list-3-line text-base" />
                  </span>
                  <span>GSTIN: 29KIZPK4767D1Z9</span>
                </li>
              </ul>
              <div className="mt-6 flex gap-2">
                {[
                  { icon: "ri-facebook-circle-line", label: "Facebook" },
                  { icon: "ri-instagram-line", label: "Instagram" },
                  { icon: "ri-whatsapp-line", label: "WhatsApp" },
                  { icon: "ri-youtube-line", label: "YouTube" },
                ].map((social) => (
                  <a
                    key={social.label}
                    href="#"
                    aria-label={social.label}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-primary-700 text-background-200 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    <i className={`${social.icon} text-lg`} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-col gap-3 border-t border-primary-700 pt-6 text-xs text-background-300 sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} Coastal Bizz Connect. All rights reserved.</p>
            <p className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link to="/" className="transition-colors hover:text-accent-300">
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className="transition-colors hover:text-accent-300">
                Terms of Service
              </Link>
              <span className="hidden sm:inline">Made on the Karnataka coast</span>
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}